// src/hooks/useDecryptedCardContent.ts
import { useEffect, useState } from "react";
import type { CardData } from "../types/card.types";
import { lrnsyCryptoService } from "../services/lrnsy-crypto.service";

type DecryptedContentState = {
  loading: boolean;
  error: string | null;
  message: string;
  personalNote: string;
  memory: string;
  insideJoke: string;
};

export function useDecryptedCardContent(card: CardData | null, senderId?: string) {
  const [state, setState] = useState<DecryptedContentState>({
    loading: false,
    error: null,
    message: "",
    personalNote: "",
    memory: "",
    insideJoke: "",
  });

  useEffect(() => {
    if (!card) return;

    const hasEncryptedContent = Boolean(card.encryptedMessage);

    if (!hasEncryptedContent) {
      setState({
        loading: false,
        error: null,
        message: card.message || "",
        personalNote: card.personalNote || "",
        memory: card.memory || "",
        insideJoke: card.insideJoke || "",
      });
      return;
    }

    const run = async () => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        let contentKey: string | null = null;

        if (card.privacyMode === "ultra_private") {
          contentKey = lrnsyCryptoService.readFragmentKey();

          if (!contentKey) {
            throw new Error(
              "This private experience could not be opened. Please ask the sender to resend the correct private link."
            );
          }
        } else {
          if (!card.encryptedKey) {
            throw new Error(
              "This private experience could not be opened. Please ask the sender to resend the correct private link."
            );
          }

          const encryptedKeyPayload = lrnsyCryptoService.parseEncryptedValue(card.encryptedKey);
          if (!encryptedKeyPayload) throw new Error("Invalid access.");

          const recoverySecret = `${senderId || card.senderId}:${
            process.env.REACT_APP_LRNSY_PUBLIC_KEY_WRAP_PEPPER || "lrnsy-standard-v1"
          }`;

          contentKey = await lrnsyCryptoService.decryptText(encryptedKeyPayload, recoverySecret);
        }

        const encryptedMessage = lrnsyCryptoService.parseEncryptedValue(card.encryptedMessage);
        if (!encryptedMessage) throw new Error("Can't be accessed.");

        const message = await lrnsyCryptoService.decryptText(encryptedMessage, contentKey);

        const encryptedPersonalNote = lrnsyCryptoService.parseEncryptedValue(
          card.encryptedPersonalNote
        );
        const encryptedMemory = lrnsyCryptoService.parseEncryptedValue(card.encryptedMemory);
        const encryptedInsideJoke = lrnsyCryptoService.parseEncryptedValue(
          card.encryptedInsideJoke
        );

        setState({
          loading: false,
          error: null,
          message,
          personalNote: encryptedPersonalNote
            ? await lrnsyCryptoService.decryptText(encryptedPersonalNote, contentKey)
            : "",
          memory: encryptedMemory
            ? await lrnsyCryptoService.decryptText(encryptedMemory, contentKey)
            : "",
          insideJoke: encryptedInsideJoke
            ? await lrnsyCryptoService.decryptText(encryptedInsideJoke, contentKey)
            : "",
        });
      } catch (error) {
        setState({
          loading: false,
          error: error instanceof Error ? error.message : "Failed to load this experience.",
          message: "",
          personalNote: "",
          memory: "",
          insideJoke: "",
        });
      }
    };

    void run();
  }, [card, senderId]);

  return state;
}

export default useDecryptedCardContent;
